import React, { useEffect, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { default as DefaultInput } from '@/components/Input';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next'; // Import the useTranslation hook
import {
  SubContractor,
  getSubContractor,
  saveSubContractor,
} from '@/services/SubContractorService';
import Spinner from '@/components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { getCompanyId } from '@/services/AuthService';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('SubContractorForm.nameRequired'),
});

interface FormValues extends SubContractor {
  name: string;
}

function SubContractorForm() {
  const { id } = useParams();
  const editForm = id !== 'new';
  const navigate = useNavigate();

  const { t } = useTranslation(); // Initialize the useTranslation hook

  const [formikValuesState, setFormikValuesState] = useState<FormValues>({
    name: '',
    id: '',
  });
  const [loading, setLoading] = useState(false);

  const {
    data: subContractorData,
    isLoading,
    refetch: refetchSubContractor,
  } = useQuery<SubContractor | undefined>(
    ['subContractor', id],
    async () => await getSubContractor(id!),
    {
      enabled: editForm,
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      onSuccess: (subContractor) => {
        if (subContractor) {
          setFormikValuesState((prev) => ({ ...prev, ...subContractor }));
        }
      },
    }
  );

  useEffect(() => {
    if (editForm && subContractorData) {
      // Update form values with subContractor data
      setFormikValuesState((prev) => ({ ...prev, ...subContractorData }));
    }
  }, [editForm, subContractorData]);

  const handleSaveSubContractor = async (
    values: FormValues,
    { resetForm, validateForm }: any
  ) => {
    setLoading(true);

    try {
      const companyId = getCompanyId();
      const subContractor: SubContractor = {
        ...values,
        companyId: companyId!,
      };

      const data = await saveSubContractor(subContractor);
      if (data) {
        if (editForm) {
          toast.success(
            t('SuccessUpdateMessage', {
              name: t('ToastMsg.SubContractor'),
            })
          );
        } else {
          toast.success(
            t('SuccessMessage', {
              name: t('ToastMsg.SubContractor'),
            })
          );
        }
      }
      if (data && editForm) {
        refetchSubContractor();
      }
      navigate(-1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={
          editForm
            ? t('SubContractorForm.editSubContractor')
            : t('SubContractorForm.addSubContractor')
        }
      />
      {loading ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={formikValuesState}
          validationSchema={validationSchema}
          onSubmit={handleSaveSubContractor}
          enableReinitialize={editForm}
        >
          {({ values, errors, handleChange, handleSubmit, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <div className="group relative z-0 mb-6 w-full">
                <DefaultInput
                  type="text"
                  name="name"
                  id="name"
                  placeholder=" "
                  onChange={handleChange}
                  errors={errors}
                  value={values.name}
                  label={t('SubContractorForm.nameLabel')}
                />
              </div>

              <button
                type="submit"
                className="focus:shadow-outline float-right mb-4 rounded-sm bg-blue-700 px-3 py-1 text-white hover:bg-blue-500 focus:outline-none"
                disabled={loading}
              >
                {t('SubContractorForm.submitButton')}
              </button>
              <button
                type="button"
                id="cancelForm"
                className="focus:shadow-outline ml-3 rounded-sm px-3 py-1 hover:bg-blue-500 hover:text-white focus:outline-none"
                onClick={() => {
                  resetForm();
                  navigate(-1);
                }}
              >
                {t('SubContractorForm.cancelButton')}
              </button>
            </form>
          )}
        </Formik>
      )}
    </DefaultLayout>
  );
}

export default SubContractorForm;
